import type { ModuleResult, SimulationResult } from '@/types/simulation-results';

const DELAYED_ANALYSIS_MS = 2 * 60 * 1000;

export type AnalysisDisplayState = 'graded' | 'analyzing' | 'delayed' | 'failed';

export interface AnalysisDisplayInfo {
  readonly state: AnalysisDisplayState;
  readonly canRetry: boolean;
}

function isReadingOrListening(module: ModuleResult['module']) {
  return module === 'reading' || module === 'listening';
}

function isWritingOrSpeaking(module: ModuleResult['module']) {
  return module === 'writing' || module === 'speaking';
}

function isOlderThanDelay(timestamp: string | null | undefined, now: number) {
  if (!timestamp) {
    return false;
  }

  const updatedAt = Date.parse(timestamp);
  return Number.isFinite(updatedAt) && now - updatedAt >= DELAYED_ANALYSIS_MS;
}

function getGradedModuleDiagnosisInfo(
  module: ModuleResult,
  now: number,
): AnalysisDisplayInfo | null {
  if (!module.is_graded || !isReadingOrListening(module.module)) {
    return null;
  }

  if (module.analysis_status === 'pending' || module.analysis_status === 'running') {
    if (isOlderThanDelay(module.analysis_updated_at, now)) {
      return { state: 'delayed', canRetry: false };
    }
    return { state: 'analyzing', canRetry: false };
  }

  if (module.analysis_status === 'failed') {
    return { state: 'failed', canRetry: module.analysis_can_retry === true };
  }

  return { state: 'graded', canRetry: false };
}

function getGradedSectionFeedbackInfo(
  module: ModuleResult,
): AnalysisDisplayInfo | null {
  if (module.section_feedback_status !== 'pending') {
    return null;
  }

  if (isWritingOrSpeaking(module.module) && module.is_graded) {
    return { state: 'analyzing', canRetry: false };
  }

  if (isReadingOrListening(module.module) && module.is_graded) {
    return { state: 'analyzing', canRetry: false };
  }

  return null;
}

export function getModuleAnalysisDisplayInfo(
  module: ModuleResult,
  now = Date.now(),
): AnalysisDisplayInfo {
  // Section feedback runs after R/L diagnostics (and after W/S grading); check it
  // before treating diagnosis/grading as fully complete.
  const sectionFeedbackPending = getGradedSectionFeedbackInfo(module);
  if (sectionFeedbackPending) {
    return sectionFeedbackPending;
  }

  const gradedDiagnosis = getGradedModuleDiagnosisInfo(module, now);
  if (gradedDiagnosis) {
    return gradedDiagnosis;
  }

  if (module.is_graded || module.analysis_status === 'succeeded') {
    return { state: 'graded', canRetry: false };
  }

  if (module.analysis_status === 'failed') {
    return { state: 'failed', canRetry: module.analysis_can_retry === true };
  }

  if (isOlderThanDelay(module.analysis_updated_at, now)) {
    return { state: 'delayed', canRetry: false };
  }

  return { state: 'analyzing', canRetry: false };
}

/** True when the learner may open the module review (answers + section feedback ready). */
export function isModuleReviewAvailable(module: ModuleResult, now = Date.now()): boolean {
  if (!module.is_graded) {
    return false;
  }
  return getModuleAnalysisDisplayInfo(module, now).state === 'graded';
}

export function shouldPollModuleAnalysis(module: ModuleResult) {
  if (module.section_feedback_status === 'pending') {
    return true;
  }

  if (module.is_graded && isReadingOrListening(module.module)) {
    return module.analysis_status === 'pending' || module.analysis_status === 'running';
  }

  if (module.is_graded || module.analysis_status === 'succeeded') {
    return false;
  }

  if (module.analysis_status === 'failed') {
    return false;
  }

  return module.analysis_status === 'pending'
    || module.analysis_status === 'running'
    || module.analysis_status === undefined
    || module.analysis_status === null;
}

export function getSimulationAnalysisDisplayInfo(result: SimulationResult): AnalysisDisplayInfo {
  const moduleStates = result.modules.map((module) => getModuleAnalysisDisplayInfo(module));

  if (moduleStates.length === 0 || moduleStates.every((state) => state.state === 'graded')) {
    return { state: 'graded', canRetry: false };
  }

  const failedState = moduleStates.find((state) => state.state === 'failed');
  if (failedState) {
    return { state: 'failed', canRetry: failedState.canRetry };
  }

  if (moduleStates.some((state) => state.state === 'delayed')) {
    return { state: 'delayed', canRetry: false };
  }

  return { state: 'analyzing', canRetry: false };
}

export function getFirstRetryableAnalysisModule(result: SimulationResult): ModuleResult | null {
  return result.modules.find((module) => {
    const info = getModuleAnalysisDisplayInfo(module);
    return info.state === 'failed' && info.canRetry;
  }) ?? null;
}

/** First module in a session that is ready for the learner review page. */
export function getFirstReviewableModuleKey(result: SimulationResult): ModuleResult['module'] | null {
  const module = result.modules.find((m) => isModuleReviewAvailable(m));
  return module?.module ?? null;
}
